import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useProtocol } from '@/hooks/useProtocol'
import { useViewProfile } from '@/hooks/useViewProfile'
import { User, Settings, UserPlus, Shuffle, ExternalLink, Sparkles, X } from 'lucide-react'
import { AccountSwitcher } from '@/components/AccountSwitcher'
import { Dropdown, DropdownMenu, DropdownMenuItem } from '@/components/Dropdown'
import { Modal } from '@/components/Modal/Modal'
import { GoToProfileSheet } from '@/components/GoToProfileSheet'
import type { ConnectedAccount } from '@/types/account'
import { MAX_ACCOUNTS } from '@/constants/accounts'
import { strings } from '@/strings'
import { useCrossProtocol } from '@/contexts/CrossProtocolContext/CrossProtocolContext'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useScrollDirection } from '@/hooks/useScrollDirection'
import { APP_HEADER_HEIGHT_PX } from '@/lib/layout'

export function Header() {
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const {
        session,
        removeAccount,
        logoutPixelfed,
        isAuthenticated,
        totalAccountCount,
        reconnectBluesky,
        reconnectError,
        clearReconnectError,
    } = useAuth()
    const { activeAccount, connectedAccounts, canCrossProtocol } = useProtocol()
    const { flags } = useFeatureFlags()
    const { showAllAccounts, setShowAllAccounts } = useCrossProtocol()
    const isHomePage = pathname === '/'
    const isSearchPage = pathname === '/search'
    const isTahquamenon = pathname.startsWith('/tahquamenon')
    const showCrossProtocolToggle =
        (isHomePage || isSearchPage) && canCrossProtocol && flags.crossProtocol
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
    const [isGoToProfileOpen, setIsGoToProfileOpen] = useState(false)
    const { profile } = useViewProfile(session?.did)

    // Hide the header on scroll-down in the home feed, mirroring the bottom nav
    // (and matching the React Native app, where the top bar scrolls away too).
    const { scrollDirection } = useScrollDirection(10, APP_HEADER_HEIGHT_PX)
    const hideHeader = isHomePage && scrollDirection === 'down'
    const headerHideClass = `transition-transform duration-300 ${
        hideHeader ? '-translate-y-full' : 'translate-y-0'
    }`

    // Desktop keyboard shortcut: Cmd+K (Mac) / Ctrl+K opens Go to Profile sheet
    useEffect(() => {
        function handleKeyDown(event: KeyboardEvent) {
            if (event.key.toLowerCase() !== 'k') return
            if (!(event.metaKey || event.ctrlKey)) return
            event.preventDefault()
            const el = document.activeElement as HTMLElement | null
            if (el) {
                const tag = el.tagName
                if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
                if (el.isContentEditable || el.contentEditable === 'true') return
            }
            setIsGoToProfileOpen(true)
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [])

    const [removeModal, setRemoveModal] = useState<{
        isOpen: boolean
        account: ConnectedAccount | null
    }>({ isOpen: false, account: null })

    /** A reconnect that failed before the redirect started (this tab only). */
    const [reconnectFailure, setReconnectFailure] = useState<string | null>(null)

    // Use active account avatar, with useViewProfile as freshness fallback for bluesky
    const avatarUrl =
        activeAccount?.platform === 'bluesky' && profile?.avatar
            ? profile.avatar
            : activeAccount?.avatar

    const handleRemoveClick = (account: ConnectedAccount) => {
        setIsProfileDropdownOpen(false)
        setRemoveModal({ isOpen: true, account })
    }

    const handleReconnectClick = (account: ConnectedAccount) => {
        setReconnectFailure(null)
        // The dropdown stays open until the redirect actually starts: a failure has
        // to be visible somewhere, and this alert lives inside the dropdown.
        // reconnectBluesky() resolves false when the user backed out or another
        // redirect is already underway — neither is an error to show.
        void reconnectBluesky(account.id)
            .then(started => {
                if (started) setIsProfileDropdownOpen(false)
            })
            .catch(err => {
                console.error('Reconnect failed:', err)
                setReconnectFailure(strings.login.reconnectFailed)
            })
    }

    const handleConfirmRemove = async () => {
        if (!removeModal.account) return
        if (removeModal.account.platform === 'pixelfed') {
            await logoutPixelfed()
        } else {
            await removeAccount(removeModal.account.id)
        }
        setRemoveModal({ isOpen: false, account: null })
    }

    if (!isAuthenticated) {
        return (
            <header
                className={`fixed top-0 left-0 right-0 h-16 px-6 flex justify-between items-center bg-white/95 dark:bg-black/80 backdrop-blur-xl border-b border-border-light dark:border-white/10 z-50 ${headerHideClass}`}
            >
                <img
                    src="/assets/logo.png"
                    alt="Mackinac"
                    className="h-8 w-auto dark:brightness-0 dark:invert"
                />
                <div />
                <button
                    onClick={() => navigate(`/login?returnTo=${encodeURIComponent(pathname)}`)}
                    className="px-4 py-1.5 rounded-full bg-blue-500 text-white text-sm font-semibold hover:bg-blue-600 transition-colors"
                >
                    {strings.publicView.signIn}
                </button>
            </header>
        )
    }

    // Counts expired placeholders too — removing one of two accounts is not a sign-out
    const isLastAccount = totalAccountCount === 1
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty displayName should fall through to handle, not render blank
    const accountDisplayName = removeModal.account?.displayName || removeModal.account?.handle || ''
    const removeLabel = removeModal.account
        ? isLastAccount
            ? strings.header.signOut
            : `Remove ${accountDisplayName}`
        : 'Remove'
    // Callback-time rejections (reconnectError, from AuthContext) and this tab's
    // pre-redirect failures share the dropdown's single alert slot.
    const dropdownError = reconnectError ?? reconnectFailure
    const dismissDropdownError = () => {
        setReconnectFailure(null)
        clearReconnectError()
    }
    const removeMessage = isLastAccount
        ? 'This is your only connected account. Removing it will sign you out.'
        : `Are you sure you want to remove ${accountDisplayName}? You can reconnect it later.`

    return (
        <>
            <Modal
                isOpen={removeModal.isOpen}
                onClose={() => setRemoveModal({ isOpen: false, account: null })}
                title={`${removeLabel}?`}
                confirmLabel={removeLabel}
                isDestructive
                onConfirm={handleConfirmRemove}
            >
                {removeMessage}
            </Modal>

            <GoToProfileSheet
                isOpen={isGoToProfileOpen}
                onClose={() => setIsGoToProfileOpen(false)}
            />

            <header
                className={`fixed top-0 left-0 right-0 h-16 px-6 flex justify-between items-center bg-white/95 dark:bg-black/80 backdrop-blur-xl border-b border-border-light dark:border-white/10 z-50 ${headerHideClass}`}
            >
                {/* Logo */}
                <img
                    src="/assets/logo.png"
                    alt="Mackinac Bridge"
                    className="h-8 w-auto dark:brightness-0 dark:invert"
                />

                {/* Empty center space */}
                <div />

                {/* Right side: cross-protocol pill + Profile dropdown */}
                <div className="flex items-center gap-2">
                    {flags.tahquamenon && (
                        <button
                            onClick={() => {
                                if (pathname.startsWith('/tahquamenon')) {
                                    void navigate(-1)
                                } else {
                                    void navigate('/tahquamenon/chat')
                                }
                            }}
                            aria-label="Tahquamenon"
                            aria-current={isTahquamenon ? 'page' : undefined}
                            className={`flex items-center justify-center p-1.5 rounded-full transition-colors ${
                                isTahquamenon
                                    ? 'bg-primary/10 text-primary'
                                    : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-light dark:hover:bg-surface-dark'
                            }`}
                        >
                            <Sparkles className="w-4 h-4" aria-hidden="true" />
                        </button>
                    )}
                    {showCrossProtocolToggle && (
                        <button
                            onClick={() => {
                                setShowAllAccounts(!showAllAccounts)
                                window.scrollTo({ top: 0, behavior: 'smooth' })
                            }}
                            aria-pressed={showAllAccounts}
                            aria-label="Toggle cross-protocol feed"
                            className={`flex items-center justify-center p-1.5 rounded-full transition-colors ${
                                showAllAccounts
                                    ? 'bg-primary/10 text-primary'
                                    : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-light dark:hover:bg-surface-dark'
                            }`}
                        >
                            <Shuffle className="w-4 h-4" aria-hidden="true" />
                        </button>
                    )}
                    {/* Profile dropdown */}
                    <Dropdown
                        isOpen={isProfileDropdownOpen}
                        onClose={() => setIsProfileDropdownOpen(false)}
                        align="right"
                        className="w-64"
                        trigger={
                            <button
                                data-testid="user-menu-trigger"
                                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                                className="flex items-center justify-center w-9 h-9 rounded-full border-2 border-white/20 hover:border-primary transition-colors overflow-hidden"
                            >
                                {avatarUrl ? (
                                    <img
                                        src={avatarUrl}
                                        alt={strings.header.userAvatarAlt}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <User className="w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark" />
                                )}
                            </button>
                        }
                    >
                        {dropdownError && (
                            <div
                                role="alert"
                                className="flex items-start gap-2 mx-2 mt-2 px-3 py-2 rounded-lg bg-red-500/10 text-xs text-red-600 dark:text-red-400"
                            >
                                <span className="flex-1">{dropdownError}</span>
                                <button
                                    onClick={dismissDropdownError}
                                    aria-label={strings.login.dismissError}
                                    className="flex-shrink-0 hover:opacity-70 transition-opacity"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                        {connectedAccounts.length > 0 && (
                            <>
                                <div className="px-3 pt-2 pb-1">
                                    <span className="text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider">
                                        Accounts
                                    </span>
                                </div>
                                <AccountSwitcher
                                    onSelect={() => setIsProfileDropdownOpen(false)}
                                    onRemove={handleRemoveClick}
                                    onReconnect={handleReconnectClick}
                                />
                            </>
                        )}
                        {totalAccountCount < MAX_ACCOUNTS && (
                            <button
                                onClick={() => {
                                    setIsProfileDropdownOpen(false)
                                    void navigate('/login?connect=true')
                                }}
                                className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg transition-colors hover:bg-border-light/30 dark:hover:bg-white/10"
                            >
                                <div className="w-7 h-7 rounded-full flex items-center justify-center bg-border-light/50 dark:bg-white/10">
                                    <UserPlus className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
                                </div>
                                <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                    {strings.header.addAccount}
                                </span>
                            </button>
                        )}
                        <div className="my-1 border-t border-border-light dark:border-border-dark" />
                        <DropdownMenu>
                            {/* Go to Profile — hidden for now, do not delete
                            <DropdownMenuItem
                                icon={<Navigation className="w-4 h-4" />}
                                onClick={() => {
                                    setIsProfileDropdownOpen(false)
                                    setIsGoToProfileOpen(true)
                                }}
                            >
                                {strings.goToProfile.sheetTitle}
                            </DropdownMenuItem>
                            */}
                            {flags.myPds && activeAccount?.platform === 'bluesky' && (
                                <DropdownMenuItem
                                    icon={<ExternalLink className="w-4 h-4" />}
                                    onClick={() => {
                                        setIsProfileDropdownOpen(false)
                                        window.open(
                                            `https://atexplore.social/${activeAccount.handle}`,
                                            '_blank',
                                            'noopener,noreferrer'
                                        )
                                    }}
                                >
                                    My PDS
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                                icon={<Settings className="w-4 h-4" />}
                                onClick={() => {
                                    setIsProfileDropdownOpen(false)
                                    void navigate('/settings')
                                }}
                            >
                                Settings
                            </DropdownMenuItem>
                        </DropdownMenu>
                    </Dropdown>
                </div>
            </header>
        </>
    )
}
